import type { Post, NewPost } from '@server/db/schema'
import type { SeoConfig } from '@shared/seo.config'

// ─────────────────────────────────────────────────────────────────────────────
// CẦU NỐI CLIENT ↔ SERVER. Đây là lợi ích lớn nhất của việc gộp hai nửa vào một
// mã nguồn: `Post` dưới đây là CHÍNH type sinh ra từ server/db/schema.ts. Đổi
// một cột trong schema thì TypeScript báo lỗi ngay tại component đang dùng cột
// đó — không còn cảnh client và server lệch nhau mà chỉ phát hiện lúc chạy.
//
// Component KHÔNG fetch trực tiếp. Thêm endpoint ở server/routes/index.ts thì
// thêm một hàm tương ứng ở đây.
// ─────────────────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  const payload = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(payload?.error ?? `HTTP ${res.status}`, res.status)
  }
  return payload as T
}

export const api = {
  health: () => request<{ ok: boolean; database: 'configured' | 'missing' }>('/api/health'),

  seo: {
    get: () => request<SeoConfig>('/api/settings/seo'),
    save: (patch: Partial<SeoConfig>) =>
      request<SeoConfig>('/api/settings/seo', { method: 'PUT', body: JSON.stringify(patch) }),
  },

  posts: {
    list: () => request<Post[]>('/api/posts'),
    get: (id: string) => request<Post>(`/api/posts/${id}`),
    create: (input: Pick<NewPost, 'title' | 'body'>) =>
      request<Post>('/api/posts', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<NewPost>) =>
      request<Post>(`/api/posts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) =>
      request<{ ok: true }>(`/api/posts/${id}`, { method: 'DELETE' }),
  },
}

export type { Post, NewPost, SeoConfig }
